import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, Crown, Flame, ArrowLeft, Scale, User, Search, Target, RefreshCw, Pencil, Check, MessageCircle, Send, Sparkles, TrendingUp, Users, StickyNote, Save } from 'lucide-react';

import { CoachWorkoutEditor } from './CoachWorkoutEditor';
import { ProgramsProEditor } from './ProgramsProEditor';
import { AIWorkoutDraftsPanel } from './AIWorkoutDraftsPanel';
import { AIHistoryPanel } from './AIHistoryPanel';
import { CoachCoaching } from './CoachCoaching';
import { CalendlyConnect } from './CalendlyConnect';
import { MeasurementComparison } from '../tracking/MeasurementComparison';
import { AdminExerciseLibraryPanel } from './AdminExerciseLibraryPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MEAL_LABELS = {
  breakfast: 'Petit-déjeuner', lunch: 'Déjeuner', snack: 'Collation', gouter: 'Goûter', dinner: 'Dîner',
};

const COACH_GOAL_LABELS = { weight_loss: 'Perte de gras', muscle_gain: 'Prise de muscle', maintenance: 'Maintien', recomposition: 'Recomposition' };
const COACH_LEVEL_LABELS = { beginner: 'Débutant', intermediate: 'Intermédiaire', advanced: 'Avancé' };
const COACH_TRAINING_TYPE_LABELS = {
  musculation: 'Musculation', crossfit: 'CrossFit', natation: 'Natation', course: 'Course à pied',
  velo: 'Vélo', marche: 'Marche rapide', boxe: 'Boxe', hiit: 'HIIT', circuit: 'Circuit training',
  ems: 'EMS', yoga: 'Yoga / Pilates', other: 'Autre',
};

const MacroPill = ({ label, val, goal, color }) => (
  <div className="rounded-xl bg-zinc-800/60 border border-white/10 p-3">
    <p className="text-xs text-zinc-400">{label}</p>
    <p className="text-lg font-bold text-white">{Math.round(val)}<span className="text-xs text-zinc-500"> / {Math.round(goal)}</span></p>
    <div className="mt-1.5 h-1.5 rounded-full bg-white/8 overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${Math.min((val / (goal || 1)) * 100, 100)}%`, background: color }} />
    </div>
  </div>
);

export const CoachPanel = ({ csrfToken, onLogout, fmtDate, initialEmail, onInitialEmailConsumed }) => {
  const [clients, setClients] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [date, setDate] = useState(null);
  const [loadingTrack, setLoadingTrack] = useState(false);
  const [q, setQ] = useState('');
  const [goalEditing, setGoalEditing] = useState(false);
  const [goalForm, setGoalForm] = useState({});
  const [goalBusy, setGoalBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState('');
  const [msgBusy, setMsgBusy] = useState(false);
  const [isTransfo, setIsTransfo] = useState(false);
  const [transfoBusy, setTransfoBusy] = useState(false);
  const [mrr, setMrr] = useState(null);
  const [clientProfile, setClientProfile] = useState(null);
  const [coachNotes, setCoachNotes] = useState('');
  const [coachNotesBusy, setCoachNotesBusy] = useState(false);
  const [coachNotesSavedAt, setCoachNotesSavedAt] = useState(null);
  const coachNotesOpenEmailRef = useRef(null);
  const coachNotesDirtyRef = useRef(false);
  
  // Helper pour les requêtes GET
  const fetchWithCredentials = (url) => fetch(url, { credentials: 'include' });
  
  // Helper pour les requêtes avec CSRF
  const fetchWithCsrf = (url, options = {}) => {
    return fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        ...options.headers,
        'X-CSRF-Token': csrfToken,
      },
    });
  };
  
  const jsonHeaders = { 'Content-Type': 'application/json' };

  useEffect(() => {
    fetchWithCredentials(`${BACKEND_URL}/api/admin/clients`)
      .then((r) => (r.status === 401 ? (onLogout(), Promise.reject()) : r.json()))
      .then((d) => setClients(d.clients || []))
      .catch(() => setClients([]));
    fetchWithCredentials(`${BACKEND_URL}/api/admin/mrr`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setMrr)
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  const loadTracking = useCallback((email, d) => {
    setLoadingTrack(true);
    setGoalEditing(false);
    const url = `${BACKEND_URL}/api/admin/clients/${encodeURIComponent(email)}/tracking${d ? `?date=${d}` : ''}`;
    fetchWithCredentials(url)
      .then((r) => r.json())
      .then((data) => { setTracking(data); setDate(data.date); })
      .finally(() => setLoadingTrack(false));
  }, []);

  const recalcGoals = () => {
    setGoalBusy(true);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/clients/${encodeURIComponent(selected.email)}/goals/recalculate`, { 
      method: 'POST'
    })
      .then((r) => (r.ok ? r.json() : r.json().then((j) => Promise.reject(j))))
      .then(() => loadTracking(selected.email, date))
      .catch((j) => window.alert(j?.detail || 'Recalcul impossible (aucun calcul pour ce client).'))
      .finally(() => setGoalBusy(false));
  };

  const saveGoals = () => {
    const payload = {};
    for (const k of ['calories', 'protein', 'carbs', 'fat']) {
      const v = parseInt(goalForm[k], 10);
      if (isNaN(v)) { window.alert('Remplissez tous les champs.'); return; }
      payload[k] = v;
    }
    setGoalBusy(true);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/clients/${encodeURIComponent(selected.email)}/goals`, { 
      method: 'PUT', 
      headers: jsonHeaders, 
      body: JSON.stringify(payload) 
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(() => { setGoalEditing(false); loadTracking(selected.email, date); })
      .catch(() => window.alert("Échec de l'enregistrement."))
      .finally(() => setGoalBusy(false));
  };

  const loadMessages = useCallback((email) => {
    fetchWithCredentials(`${BACKEND_URL}/api/admin/clients/${encodeURIComponent(email)}/messages`)
      .then((r) => r.json())
      .then((d) => { setMessages(d.messages || []); setIsTransfo(!!d.transformation); })
      .catch(() => {});
  }, []);

  const sendCoachMessage = () => {
    if (!msgText.trim()) return;
    setMsgBusy(true);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/clients/${encodeURIComponent(selected.email)}/messages`, { 
      method: 'POST', 
      headers: jsonHeaders, 
      body: JSON.stringify({ text: msgText.trim() }) 
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(() => { setMsgText(''); loadMessages(selected.email); })
      .catch(() => window.alert("Échec de l'envoi."))
      .finally(() => setMsgBusy(false));
  };

  const toggleTransfo = () => {
    const action = isTransfo ? 'unenroll-transformation' : 'enroll-transformation';
    setTransfoBusy(true);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/clients/${encodeURIComponent(selected.email)}/${action}`, { 
      method: 'POST'
    })
      .then((r) => r.json())
      .then((d) => setIsTransfo(!!d.transformation))
      .finally(() => setTransfoBusy(false));
  };

  const openClient = (c) => {
    setSelected(c); setTracking(null); setMessages([]); setIsTransfo(!!c.transformation); setClientProfile(null);
    setCoachNotes(''); setCoachNotesSavedAt(null);
    coachNotesOpenEmailRef.current = c.email; coachNotesDirtyRef.current = false;
    loadTracking(c.email, null); loadMessages(c.email);
    fetchWithCredentials(`${BACKEND_URL}/api/admin/programs-pro/client-profile-full/${encodeURIComponent(c.email)}`)
      .then((r) => (r.ok ? r.json() : null)).then(setClientProfile).catch(() => {});
    fetchWithCredentials(`${BACKEND_URL}/api/admin/programs-pro/client-notes/${encodeURIComponent(c.email)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        // Ignore une réponse tardive si le coach a changé de client ou déjà commencé à taper.
        if (!d || coachNotesOpenEmailRef.current !== c.email || coachNotesDirtyRef.current) return;
        setCoachNotes(d.notes || ''); setCoachNotesSavedAt(d.updated_at || null);
      }).catch(() => {});
  };

  const saveCoachNotes = () => {
    setCoachNotesBusy(true);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/programs-pro/client-notes/${encodeURIComponent(selected.email)}`, {
      method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ notes: coachNotes }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => { setCoachNotesSavedAt(d.updated_at); coachNotesDirtyRef.current = false; })
      .catch(() => window.alert("Échec de l'enregistrement de la note."))
      .finally(() => setCoachNotesBusy(false));
  };

  // Pré-sélection d'un client venant de l'onglet "Clients" (fiche 360°)
  useEffect(() => {
    if (initialEmail && clients) {
      const match = clients.find((c) => c.email === initialEmail);
      if (match) openClient(match);
      onInitialEmailConsumed && onInitialEmailConsumed();
    }
    // eslint-disable-next-line
  }, [initialEmail, clients]);


  if (selected) {
    const s = tracking?.summary;
    const entries = tracking?.entries || [];
    const lastWeight = (tracking?.weights || []).slice(-1)[0];
    return (
      <div data-testid="coach-client-detail">
        <button onClick={() => { setSelected(null); setTracking(null); }} className="flex items-center gap-2 text-zinc-400 hover:text-primary text-sm mb-4" data-testid="coach-back">
          <ArrowLeft className="w-4 h-4" /> Retour aux clients
        </button>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              {selected.name || selected.email}
              {selected.premium && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-black bg-gradient-to-r from-yellow-400 to-primary px-2 py-0.5 rounded-full"><Crown className="w-3 h-3" /> PRO</span>}
              {isTransfo && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-white bg-gradient-to-r from-purple-500 to-primary px-2 py-0.5 rounded-full" data-testid="coach-transfo-badge"><Sparkles className="w-3 h-3" /> TRANSFORMATION</span>}
            </h2>
            <p className="text-xs text-zinc-500">{selected.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleTransfo} disabled={transfoBusy}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-transform hover:scale-105 disabled:opacity-50 ${isTransfo ? 'text-zinc-300 border border-white/15' : 'text-white bg-gradient-to-r from-purple-500 to-primary'}`}
              data-testid="coach-toggle-transfo">
              {transfoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {isTransfo ? 'Retirer Transformation' : 'Inscrire Transformation'}
            </button>
            {tracking?.logged_dates?.length > 0 && (
              <select value={date || ''} onChange={(e) => loadTracking(selected.email, e.target.value)} className="bg-zinc-800 border border-white/10 rounded-xl px-3 py-2 text-white text-sm" data-testid="coach-date-select">
                {tracking.logged_dates.map((d) => <option key={d} value={d}>{fmtDate(d)}</option>)}
              </select>
            )}
          </div>
        </div>

        {clientProfile && clientProfile.has_profile && (
          <div className="rounded-2xl bg-zinc-900/60 border border-white/10 p-4 mb-5 flex flex-wrap gap-x-6 gap-y-2" data-testid="coach-profile-summary">
            <p className="text-xs font-bold text-zinc-400 uppercase tracking-wide w-full flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Résumé profil (calculateur)</p>
            {clientProfile.age && <span className="text-sm text-zinc-300"><span className="text-zinc-500">Âge</span> {clientProfile.age} ans</span>}
            {clientProfile.height && <span className="text-sm text-zinc-300"><span className="text-zinc-500">Taille</span> {clientProfile.height} cm</span>}
            {clientProfile.weight && <span className="text-sm text-zinc-300"><span className="text-zinc-500">Poids</span> {clientProfile.weight} kg</span>}
            {clientProfile.target_weight && <span className="text-sm text-zinc-300"><span className="text-zinc-500">Objectif poids</span> {clientProfile.target_weight} kg</span>}
            {clientProfile.goal && <span className="text-sm text-zinc-300"><span className="text-zinc-500">Objectif</span> {COACH_GOAL_LABELS[clientProfile.goal] || clientProfile.goal}</span>}
            {clientProfile.training_frequency != null && <span className="text-sm text-zinc-300"><span className="text-zinc-500">Séances/sem.</span> {clientProfile.training_frequency}</span>}
            {clientProfile.training_type && <span className="text-sm text-zinc-300"><span className="text-zinc-500">Activité</span> {COACH_TRAINING_TYPE_LABELS[clientProfile.training_type] || clientProfile.training_type}</span>}
            {clientProfile.training_level && <span className="text-sm text-zinc-300"><span className="text-zinc-500">Niveau</span> {COACH_LEVEL_LABELS[clientProfile.training_level] || clientProfile.training_level}</span>}
            {clientProfile.allergies && <span className="text-sm text-red-300"><span className="text-zinc-500">Allergies</span> {clientProfile.allergies}</span>}
            {clientProfile.diet_preferences && <span className="text-sm text-zinc-300"><span className="text-zinc-500">Préférences</span> {clientProfile.diet_preferences}</span>}
          </div>
        )}

        <div className="rounded-2xl bg-zinc-900/60 border border-white/10 p-4 mb-5" data-testid="coach-notes-section">
          <p className="text-xs font-bold text-zinc-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><StickyNote className="w-3.5 h-3.5" /> Notes internes coach (jamais visibles du client)</p>
          <textarea
            value={coachNotes}
            onChange={(e) => { coachNotesDirtyRef.current = true; setCoachNotes(e.target.value); }}
            placeholder="Ex: préfère les séances le matin, sensible au genou droit, motivée par les défis..."
            className="w-full min-h-[90px] rounded-xl bg-zinc-800/60 border border-white/10 p-3 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-primary/50 resize-y"
            data-testid="coach-notes-textarea"
          />
          <div className="flex items-center justify-between mt-2">
            <p className="text-[11px] text-zinc-500">{coachNotesSavedAt ? `Enregistré le ${fmtDate(coachNotesSavedAt)}` : 'Pas encore enregistré'}</p>
            <button onClick={saveCoachNotes} disabled={coachNotesBusy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-primary hover:scale-105 transition-transform disabled:opacity-50"
              data-testid="coach-notes-save-btn">
              {coachNotesBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Enregistrer la note
            </button>
          </div>
        </div>


        {loadingTrack ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
        ) : s ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              <div className="rounded-xl bg-gradient-to-br from-primary/15 to-black border border-primary/20 p-4">
                <Flame className="w-5 h-5 text-primary mb-1" />
                <p className="text-2xl font-extrabold text-white">{Math.round(s.totals.calories)}<span className="text-xs text-zinc-500"> / {Math.round(s.goals.calories)}</span></p>
                <p className="text-xs text-zinc-400">Calories</p>
              </div>
              <MacroPill label="Protéines (g)" val={s.totals.protein} goal={s.goals.protein} color="#EC4899" />
              <MacroPill label="Glucides (g)" val={s.totals.carbs} goal={s.goals.carbs} color="#FBBF24" />
              <MacroPill label="Lipides (g)" val={s.totals.fat} goal={s.goals.fat} color="#10B981" />
            </div>
            <div className="flex items-center gap-4 mb-5 text-sm text-zinc-300">
              <span>Score santé : <strong className="text-white">{s.health_score ?? '—'}/100</strong></span>
              {lastWeight && <span className="inline-flex items-center gap-1.5"><Scale className="w-4 h-4 text-primary" /> {lastWeight.weight} kg ({fmtDate(lastWeight.date)})</span>}
            </div>

            {/* Objectifs (macros) — édition coach */}
            <div className="bg-zinc-900 border border-white/10 rounded-2xl p-4 mb-5" data-testid="coach-goals">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="flex items-center gap-2 font-bold text-white text-sm"><Target className="w-4 h-4 text-primary" /> Objectifs (macros)</h3>
                {!goalEditing && (
                  <div className="flex items-center gap-2">
                    <button onClick={recalcGoals} disabled={goalBusy || !tracking?.has_calculation}
                      title={tracking?.has_calculation ? '' : 'Aucun calcul disponible pour ce client'}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-105 transition-transform disabled:opacity-50"
                      data-testid="coach-recalc-goals">
                      {goalBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Recalculer (poids)
                    </button>
                    <button onClick={() => { setGoalForm({ ...s.goals }); setGoalEditing(true); }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-300 border border-white/10 hover:border-primary/50 hover:text-white transition-colors"
                      data-testid="coach-edit-goals">
                      <Pencil className="w-3.5 h-3.5" /> Modifier
                    </button>
                  </div>
                )}
              </div>
              {!goalEditing ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[['calories', 'Calories', 'kcal'], ['protein', 'Protéines', 'g'], ['carbs', 'Glucides', 'g'], ['fat', 'Lipides', 'g']].map(([k, lab, u]) => (
                    <div key={k} className="rounded-xl bg-zinc-800/60 border border-white/10 p-3">
                      <p className="text-xs text-zinc-400">{lab}</p>
                      <p className="text-lg font-bold text-white">{Math.round(s.goals[k])}<span className="text-xs text-zinc-500"> {u}</span></p>
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[['calories', 'Calories'], ['protein', 'Protéines'], ['carbs', 'Glucides'], ['fat', 'Lipides']].map(([k, lab]) => (
                      <div key={k}>
                        <label className="text-xs text-zinc-400 mb-1 block">{lab}</label>
                        <input type="number" value={goalForm[k] ?? ''} onChange={(e) => setGoalForm((f) => ({ ...f, [k]: e.target.value }))}
                          className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-primary focus:outline-none"
                          data-testid={`coach-goal-input-${k}`} />
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={saveGoals} disabled={goalBusy} className="px-4 py-2 rounded-lg font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-50 flex items-center gap-2 text-sm" data-testid="coach-save-goals">
                      {goalBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Enregistrer
                    </button>
                    <button onClick={() => setGoalEditing(false)} className="px-4 py-2 rounded-lg text-sm font-semibold text-zinc-300 border border-white/10 hover:text-white transition-colors">Annuler</button>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-zinc-900 border border-white/10 rounded-2xl overflow-hidden">
              <table className="w-full text-sm" data-testid="coach-entries-table">
                <thead className="bg-zinc-800/70 text-zinc-400">
                  <tr>{['Repas', 'Aliment', 'Qté', 'kcal', 'P', 'G', 'L'].map((h) => <th key={h} className="text-left px-4 py-2.5 font-semibold whitespace-nowrap">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {entries.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">Aucune entrée ce jour-là</td></tr>
                  ) : entries.map((e, i) => (
                    <tr key={i} className="border-t border-white/5 text-zinc-300">
                      <td className="px-4 py-2.5 whitespace-nowrap text-zinc-500">{MEAL_LABELS[e.meal] || e.meal}</td>
                      <td className="px-4 py-2.5 text-white">{e.food_name}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">{e.quantity} {e.unit}</td>
                      <td className="px-4 py-2.5 font-semibold text-primary">{Math.round(e.calories)}</td>
                      <td className="px-4 py-2.5">{Math.round(e.protein)}</td>
                      <td className="px-4 py-2.5">{Math.round(e.carbs)}</td>
                      <td className="px-4 py-2.5">{Math.round(e.fat)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : <p className="text-zinc-500 text-sm py-8 text-center">Aucune donnée de suivi.</p>}

        {/* Messagerie coach ↔ client */}
        <div className="bg-zinc-900 border border-white/10 rounded-2xl p-4 mt-5" data-testid="coach-messaging">
          <h3 className="flex items-center gap-2 font-bold text-white text-sm mb-3"><MessageCircle className="w-4 h-4 text-primary" /> Messagerie {!isTransfo && <span className="text-xs font-normal text-zinc-500">(client hors Transformation)</span>}</h3>
          <div className="h-64 overflow-y-auto space-y-2 mb-3 pr-1 flex flex-col" data-testid="coach-messages-list">
            {messages.length === 0 ? (
              <p className="text-zinc-600 text-sm text-center my-auto">Aucun message pour l'instant.</p>
            ) : messages.map((m) => (
              <div key={m.message_id} className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${m.sender === 'coach' ? 'self-end bg-gradient-to-r from-primary to-pink-500 text-white' : 'self-start bg-zinc-800 text-zinc-200 border border-white/10'}`}>
                {m.text}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={msgText} onChange={(e) => setMsgText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') sendCoachMessage(); }}
              placeholder="Répondre au client…" className="flex-1 bg-zinc-800 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:border-primary focus:outline-none" data-testid="coach-message-input" />
            <button onClick={sendCoachMessage} disabled={msgBusy || !msgText.trim()} className="px-4 py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-50 flex items-center gap-2" data-testid="coach-send-message">
              {msgBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <ProgramsProEditor email={selected.email} csrfToken={csrfToken} clients={clients} />
        <CoachWorkoutEditor email={selected.email} csrfToken={csrfToken} />
        <CoachCoaching email={selected.email} csrfToken={csrfToken} />

        {/* Comparateur de mensurations pro (vue coach) */}
        {(tracking?.weights || []).length >= 2 ? (
          <div className="bg-zinc-900 border border-white/10 rounded-2xl p-4 mt-5" data-testid="coach-measurement-comparison-wrap">
            <MeasurementComparison entries={tracking.weights} en={false} />
          </div>
        ) : (
          <div className="bg-zinc-900 border border-white/10 rounded-2xl p-4 mt-5 text-center text-zinc-500 text-sm" data-testid="coach-measurement-comparison-locked">
            Le client doit avoir au moins 2 mensurations enregistrées pour afficher le comparateur pro.
          </div>
        )}
      </div>
    );
  }

  const filtered = (clients || []).filter((c) => !q || (c.email + ' ' + (c.name || '')).toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="coach-panel">
      <CalendlyConnect csrfToken={csrfToken} />
      <AdminExerciseLibraryPanel csrfToken={csrfToken} />
      <AIWorkoutDraftsPanel csrfToken={csrfToken} clients={clients} />
      <AIHistoryPanel csrfToken={csrfToken} clients={clients} />
      {mrr && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="coach-mrr">
          <div className="rounded-2xl bg-gradient-to-br from-purple-500/15 to-black border border-purple-500/30 p-4">
            <TrendingUp className="w-5 h-5 text-purple-300 mb-1" />
            <p className="text-2xl font-extrabold text-white">{mrr.mrr.toLocaleString('fr-CH')} <span className="text-sm text-zinc-400">CHF</span></p>
            <p className="text-xs text-zinc-400">MRR (Transformation)</p>
          </div>
          <div className="rounded-2xl bg-zinc-900 border border-white/10 p-4">
            <p className="text-2xl font-extrabold text-white">{mrr.arr.toLocaleString('fr-CH')} <span className="text-sm text-zinc-400">CHF</span></p>
            <p className="text-xs text-zinc-400 mt-auto">ARR (annualisé)</p>
          </div>
          <div className="rounded-2xl bg-zinc-900 border border-white/10 p-4">
            <Users className="w-5 h-5 text-primary mb-1" />
            <p className="text-2xl font-extrabold text-white">{mrr.active_members}</p>
            <p className="text-xs text-zinc-400">Membres actifs</p>
          </div>
          <div className="rounded-2xl bg-zinc-900 border border-white/10 p-4 flex flex-col justify-center">
            <p className="text-sm text-zinc-300">{mrr.monthly_subs} mensuel{mrr.monthly_subs > 1 ? 's' : ''}</p>
            <p className="text-sm text-zinc-300">{mrr.upfront_subs} pré-payé{mrr.upfront_subs > 1 ? 's' : ''}</p>
            <p className="text-xs text-zinc-500 mt-1">Répartition</p>
          </div>
        </div>
      )}
      <div className="relative mb-4 max-w-xs">
        <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un client…" className="w-full bg-zinc-800 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-white text-sm focus:border-primary focus:outline-none" data-testid="coach-search" />
      </div>
      <div className="bg-zinc-900 border border-white/10 rounded-2xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="coach-clients-table">
          <thead className="bg-zinc-800/70 text-zinc-400">
            <tr>{['Client', 'Email', 'Statut', 'Entrées', 'Poids', 'Dernier journal', ''].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {clients === null ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center"><Loader2 className="w-6 h-6 text-primary animate-spin mx-auto" /></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">Aucun client</td></tr>
            ) : filtered.map((c, i) => (
              <tr key={i} className="border-t border-white/5 text-zinc-300 hover:bg-zinc-800/40">
                <td className="px-4 py-3 whitespace-nowrap text-white"><div className="flex items-center gap-2"><User className="w-4 h-4 text-zinc-500" /> {c.name || '—'}{c.unread > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="coach-unread-badge">{c.unread}</span>}</div></td>
                <td className="px-4 py-3 whitespace-nowrap">{c.email}</td>
                <td className="px-4 py-3"><div className="flex items-center gap-1">{c.premium ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-black bg-gradient-to-r from-yellow-400 to-primary px-2 py-0.5 rounded-full"><Crown className="w-3 h-3" /> PRO</span> : <span className="text-zinc-500 text-xs">Gratuit</span>}{c.transformation && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-white bg-gradient-to-r from-purple-500 to-primary px-2 py-0.5 rounded-full"><Sparkles className="w-3 h-3" /></span>}</div></td>
                <td className="px-4 py-3">{c.log_count}</td>
                <td className="px-4 py-3">{c.weight_count}</td>
                <td className="px-4 py-3 whitespace-nowrap text-zinc-500">{c.last_log_date ? fmtDate(c.last_log_date) : '—'}</td>
                <td className="px-4 py-3">
                  <button onClick={() => openClient(c)} className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-105 transition-transform" data-testid="coach-view-btn">Voir le suivi</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CoachPanel;
